import React from 'react';
import { Link } from 'react-router-dom';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div
        className="py-14 px-6 text-white"
        style={{
          background: 'linear-gradient(135deg, #F06F6B 0%, #F07A5E 40%, #F58A54 100%)',
        }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-1">Cookie Policy</h1>
          <p className="text-white/90 text-base font-medium mb-1">Stackd</p>
          <p className="text-white/80 text-sm">Last Updated: 3/12/2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This Cookie Policy explains how Stackd. ("Stackd Financial," "Stackd," "we," "our," or "us") uses cookies and similar technologies when you visit or use the Stackd website, applications, and related services (collectively, the "Platform").
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cookies help us improve the functionality of our Platform, understand how users interact with it, and support integrations with partners and service providers.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By continuing to use the Platform, you consent to the use of cookies and similar technologies as described in this policy.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">2. What Are Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cookies are small text files that are stored on your device when you visit a website. They help websites recognize your device, remember preferences, and analyze how visitors interact with the platform.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Cookies may be stored for a single browsing session (session cookies) or remain on your device for a longer period (persistent cookies).
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">3. Types of Cookies We Use</h2>

            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Essential Cookies</h3>
                <p className="text-gray-700 leading-relaxed">
                  These cookies are necessary for the basic functionality of the Platform. They allow you to navigate the site, access secure areas, and use core features.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Performance and Analytics Cookies</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  These cookies help us understand how users interact with the Platform. They collect information such as:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 pl-2 mb-3">
                  <li>Pages visited</li>
                  <li>Time spent on pages</li>
                  <li>Navigation patterns</li>
                  <li>Errors encountered</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  This information helps us improve platform performance and usability.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Functionality Cookies</h3>
                <p className="text-gray-700 leading-relaxed">
                  These cookies remember user preferences such as location settings, saved information, or customization choices that improve your experience when returning to the Platform.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Affiliate and Referral Cookies</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Some cookies may be used to track referral activity when users click outbound links to third‑party partners. These cookies allow partners to recognize that traffic originated from Stackd and may enable referral compensation for completed actions.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  These cookies are typically managed by third‑party affiliate networks or partners.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">4. Third‑Party Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Stackd may work with trusted third‑party services that use cookies to support analytics, platform functionality, or affiliate partnerships. These may include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 pl-2 mb-4">
              <li>Analytics providers</li>
              <li>Advertising networks</li>
              <li>Affiliate tracking platforms</li>
              <li>Technology service providers</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-3">
              These third parties may place cookies on your device when you interact with the Platform or follow links to their services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Each third party maintains its own privacy and cookie policies.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">5. How You Can Manage Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Most web browsers allow users to control cookie behavior through browser settings.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">You can choose to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 pl-2 mb-4">
              <li>Block cookies</li>
              <li>Delete existing cookies</li>
              <li>Receive alerts when cookies are placed</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-3">
              Please note that disabling certain cookies may affect the functionality of the Platform.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Instructions for managing cookies can typically be found in your browser's help settings.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">6. Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Stackd may update this Cookie Policy from time to time. Updates will be posted on this page with a revised "Last Updated" date.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Your continued use of the Platform after changes are posted indicates acceptance of the updated policy.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">7. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              If you have questions about this Cookie Policy, please contact:
            </p>
            <p className="text-gray-700 leading-relaxed">
              Email:{' '}
              <a
                href="mailto:info@saveonstackd.com"
                className="text-orange-500 hover:text-orange-600 transition-colors"
              >
                info@saveonstackd.com
              </a>
            </p>
          </section>

          {/* Back link */}
          <div className="mt-10 pt-6 border-t border-gray-100">
            <Link
              to="/"
              className="text-sm font-medium text-orange-500 hover:text-orange-600 transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
